import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Code, 
  Copy, 
  Check,
  ExternalLink,
  Smartphone,
  Monitor
} from "lucide-react";
import { toast } from "sonner";

const BookingWidget = ({ stylistId, stylistName }) => {
  const [copied, setCopied] = useState(null);
  const [previewMode, setPreviewMode] = useState("desktop");
  
  const baseUrl = window.location.origin;
  const widgetUrl = `${baseUrl}/booking?stylist=${stylistId}`;
  
  // Iframe embed code
  const iframeCode = `<iframe
  src="${widgetUrl}&embed=true"
  width="100%"
  height="600"
  frameborder="0"
  style="border-radius: 16px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);"
  title="Book with ${stylistName || 'Stylist'}"
></iframe>`;

  // JavaScript widget code
  const jsWidgetCode = `<!-- MyHairChair Booking Widget -->
<div id="chairpro-booking-widget"></div>
<script>
(function() {
  var widget = document.getElementById('chairpro-booking-widget');
  var iframe = document.createElement('iframe');
  iframe.src = '${widgetUrl}&embed=true';
  iframe.style.width = '100%';
  iframe.style.height = '600px';
  iframe.style.border = 'none';
  iframe.style.borderRadius = '16px';
  iframe.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
  widget.appendChild(iframe);
})();
</script>`;

  // Button link code
  const buttonCode = `<a href="${widgetUrl}" target="_blank" rel="noopener noreferrer" style="
  display: inline-block;
  padding: 12px 24px;
  background: linear-gradient(135deg, #f97316, #ef4444, #f59e0b);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-family: system-ui, sans-serif;
  box-shadow: 0 4px 12px rgba(249,115,22,0.3);
">Book with ${stylistName || 'Me'}</a>`;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Instant Booking Widget</h2>
        <p className="text-gray-600">Add booking to your Instagram, Facebook, or website</p>
      </div>

      {/* Direct Link */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-orange-500" />
            Direct Booking Link
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">
            Share this link on social media or anywhere else
          </p>
          <div className="flex gap-2">
            <Input 
              value={widgetUrl} 
              readOnly 
              className="flex-1 bg-gray-50"
            />
            <Button 
              variant="outline"
              onClick={() => copyToClipboard(widgetUrl, "link")}
              className="flex items-center gap-2"
            >
              {copied === "link" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Iframe Embed */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Code className="w-5 h-5 text-blue-500" />
            Website Embed (iFrame)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">
            Copy this code and paste it into your website HTML
          </p>
          <div className="relative">
            <pre className="bg-gray-900 text-green-400 p-4 rounded-xl text-xs overflow-x-auto">
              {iframeCode}
            </pre>
            <Button 
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(iframeCode, "iframe")}
            >
              {copied === "iframe" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* JavaScript Widget */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Code className="w-5 h-5 text-purple-500" />
            JavaScript Widget
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">
            Advanced embed with automatic sizing
          </p>
          <div className="relative">
            <pre className="bg-gray-900 text-green-400 p-4 rounded-xl text-xs overflow-x-auto max-h-40">
              {jsWidgetCode}
            </pre>
            <Button 
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(jsWidgetCode, "js")}
            >
              {copied === "js" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Button Link */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-orange-500" />
            Booking Button
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-3">
            Add a styled booking button to any page
          </p>
          
          {/* Preview */}
          <div className="mb-4 p-6 bg-gray-100 rounded-xl flex justify-center">
            <a 
              href={widgetUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/30 hover:shadow-xl transition-shadow"
            >
              Book with {stylistName || 'Me'}
            </a>
          </div>

          <div className="relative">
            <pre className="bg-gray-900 text-green-400 p-4 rounded-xl text-xs overflow-x-auto">
              {buttonCode}
            </pre>
            <Button 
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2"
              onClick={() => copyToClipboard(buttonCode, "button")}
            >
              {copied === "button" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Preview</CardTitle>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant={previewMode === "desktop" ? "default" : "outline"}
                onClick={() => setPreviewMode("desktop")}
              >
                <Monitor className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant={previewMode === "mobile" ? "default" : "outline"}
                onClick={() => setPreviewMode("mobile")}
              >
                <Smartphone className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`mx-auto border-4 border-gray-200 rounded-2xl overflow-hidden bg-white ${
            previewMode === "mobile" ? "max-w-xs" : "max-w-full"
          }`}>
            <iframe
              src={`${widgetUrl}&embed=true`}
              className="w-full"
              style={{ height: previewMode === "mobile" ? "500px" : "400px" }}
              title="Booking Widget Preview"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingWidget;
